import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Save,
  Loader2,
  Send,
  ArrowLeft,
  Car,
  Euro
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ObjectiveFormPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [objective, setObjective] = useState(null);
  
  const isEditing = !!id;
  const preselectedSeller = searchParams.get("seller");
  const preselectedMonth = searchParams.get("month");
  const preselectedYear = searchParams.get("year");
  
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [formData, setFormData] = useState({
    seller_id: preselectedSeller || "",
    concession_id: user?.concession_id || "",
    month: preselectedMonth ? parseInt(preselectedMonth) : currentMonth,
    year: preselectedYear ? parseInt(preselectedYear) : currentYear,
    vn_target: 0,
    vo_target: 0,
    financing_target: 0,
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const requests = [
        api.get("/users", { params: { role: "seller" } }),
        user?.role === "admin" ? api.get("/concessions") : Promise.resolve({ data: [] })
      ];
      
      if (isEditing) {
        requests.push(api.get(`/objectives/${id}`));
      }

      const responses = await Promise.all(requests);
      setSellers(responses[0].data);
      setConcessions(responses[1].data);
      
      if (isEditing && responses[2]) {
        const obj = responses[2].data;
        setObjective(obj);
        setFormData({
          seller_id: obj.seller_id,
          concession_id: obj.concession_id,
          month: obj.month,
          year: obj.year,
          vn_target: obj.vn_target || 0,
          vo_target: obj.vo_target || 0,
          financing_target: obj.financing_target || 0,
          notes: obj.notes || ""
        });
      }
    } catch (error) {
      toast.error("Erreur lors du chargement");
      if (error.response?.status === 404) {
        navigate("/objectives");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (andSend = false) => {
    if (!formData.seller_id) {
      toast.error("Veuillez sélectionner un vendeur");
      return;
    }

    // Set concession_id from seller if not admin
    let dataToSend = { ...formData };
    if (user?.role === "manager") {
      dataToSend.concession_id = user.concession_id;
    } else if (!dataToSend.concession_id) {
      const seller = sellers.find(s => s.id === formData.seller_id);
      dataToSend.concession_id = seller?.concession_id;
    }

    setSaving(true);
    try {
      let objectiveId = id;
      
      if (isEditing) {
        await api.put(`/objectives/${id}`, dataToSend);
        toast.success("Objectif mis à jour");
      } else {
        const res = await api.post("/objectives", dataToSend);
        objectiveId = res.data.id;
        toast.success("Objectif créé");
      }

      if (andSend) {
        setSending(true);
        await api.post(`/objectives/${objectiveId}/send-for-signature`);
        toast.success("Objectif envoyé pour signature");
      }

      navigate("/objectives");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
      setSending(false);
    }
  };

  const monthNames = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  const canEdit = !objective || ["draft", "pending_seller"].includes(objective?.status);

  if (loading) {
    return (
      <Layout title="Objectif">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? "Modifier l'objectif" : "Nouvel objectif"}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/objectives")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        {objective && (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            objective.status === "draft" ? "bg-gray-100 text-gray-700" :
            objective.status === "pending_seller" ? "bg-amber-100 text-amber-700" :
            objective.status === "pending_manager" ? "bg-blue-100 text-blue-700" :
            "bg-green-100 text-green-700"
          }`}>
            {objective.status === "draft" && "Brouillon"}
            {objective.status === "pending_seller" && "En attente vendeur"}
            {objective.status === "pending_manager" && "En attente responsable"}
            {objective.status === "signed" && "Signé"}
          </span>
        )}
      </div>

      <div className="max-w-2xl">
        {/* Informations générales */}
        <div className="form-section">
          <h3 className="form-section-title flex items-center gap-2">
            Informations générales
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Vendeur *</Label>
              <Select 
                value={formData.seller_id} 
                onValueChange={(v) => updateField("seller_id", v)}
                disabled={!canEdit || isEditing}
              >
                <SelectTrigger data-testid="seller-select">
                  <SelectValue placeholder="Sélectionner un vendeur" />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mois *</Label>
              <Select 
                value={formData.month.toString()} 
                onValueChange={(v) => updateField("month", parseInt(v))}
                disabled={!canEdit || isEditing}
              >
                <SelectTrigger data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.slice(1).map((month, index) => (
                    <SelectItem key={index + 1} value={(index + 1).toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Année *</Label>
              <Select 
                value={formData.year.toString()} 
                onValueChange={(v) => updateField("year", parseInt(v))}
                disabled={!canEdit || isEditing}
              >
                <SelectTrigger data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Objectif VN */}
        <div className="form-section">
          <h3 className="form-section-title flex items-center gap-2">
            <Car className="w-5 h-5" />
            Objectif VN (Véhicules Neufs)
          </h3>
          <div className="space-y-2">
            <Label>Nombre de véhicules neufs à vendre</Label>
            <Input
              type="number"
              value={formData.vn_target}
              onChange={(e) => updateField("vn_target", parseInt(e.target.value) || 0)}
              disabled={!canEdit}
              placeholder="Ex: 10"
              className="max-w-xs"
              data-testid="vn-target-input"
            />
          </div>
        </div>

        {/* Objectif VO */}
        <div className="form-section">
          <h3 className="form-section-title flex items-center gap-2">
            <Car className="w-5 h-5" />
            Objectif VO (Véhicules d'Occasion)
          </h3>
          <div className="space-y-2">
            <Label>Nombre de véhicules d'occasion à vendre</Label>
            <Input
              type="number"
              value={formData.vo_target}
              onChange={(e) => updateField("vo_target", parseInt(e.target.value) || 0)}
              disabled={!canEdit}
              placeholder="Ex: 15"
              className="max-w-xs"
              data-testid="vo-target-input"
            />
          </div>
        </div>

        {/* Objectif Financement */}
        <div className="form-section">
          <h3 className="form-section-title flex items-center gap-2">
            <Euro className="w-5 h-5" />
            Objectif Financement
          </h3>
          <div className="space-y-2">
            <Label>Chiffre d'affaires financement (€)</Label>
            <Input
              type="number"
              value={formData.financing_target}
              onChange={(e) => updateField("financing_target", parseFloat(e.target.value) || 0)}
              disabled={!canEdit}
              placeholder="Ex: 50000"
              className="max-w-xs"
              data-testid="financing-target-input"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="form-section">
          <h3 className="form-section-title">Notes</h3>
          <Textarea
            value={formData.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            placeholder="Ajoutez des notes ou commentaires..."
            rows={4}
            disabled={!canEdit}
            data-testid="notes-input"
          />
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <Button
              onClick={() => handleSave(false)}
              className="btn-secondary flex-1"
              disabled={saving}
              data-testid="save-draft-btn"
            >
              {saving && !sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer brouillon
                </>
              )}
            </Button>
            <Button
              onClick={() => handleSave(true)}
              className="btn-primary flex-1"
              disabled={saving || sending}
              data-testid="save-and-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enregistrer et envoyer pour signature
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
